
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { COUNTRIES } from "@/lib/countries";

interface AuthFormProps {
  email: string;
  setEmail: (email: string) => void;
  password: string;
  setPassword: (password: string) => void;
  username: string;
  setUsername: (username: string) => void;
  lastName: string;
  setLastName: (lastName: string) => void;
  firstName: string;
  setFirstName: (firstName: string) => void;
  residenceCountry: string;
  setResidenceCountry: (country: string) => void;
  loading: boolean;
  isSignUp: boolean;
  setIsSignUp: (isSignUp: boolean) => void;
  handleAuth: (e: React.FormEvent) => void;
  handleResetPassword: () => void;
}

export const AuthForm = ({
  email,
  setEmail,
  password,
  setPassword,
  username,
  setUsername,
  lastName,
  setLastName,
  firstName,
  setFirstName,
  residenceCountry,
  setResidenceCountry,
  loading,
  isSignUp,
  setIsSignUp,
  handleAuth,
  handleResetPassword
}: AuthFormProps) => {
  const [forgotPassword, setForgotPassword] = useState(false);
  const { t } = useTranslation('auth');

  if (forgotPassword) {
    return (
      <form onSubmit={(e) => {
        e.preventDefault();
        handleResetPassword();
      }} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="reset-email">{t('fields.email')}</Label>
          <Input
            id="reset-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        <Button type="submit" className="w-full" disabled={loading}>
          {loading ? t('buttons.loading', { ns: 'common' }) : t('buttons.resetPassword')}
        </Button>

        <Button
          type="button"
          variant="ghost"
          className="w-full"
          onClick={() => setForgotPassword(false)}
        >
          {t('buttons.backToLogin')}
        </Button>
      </form>
    );
  }

  return (
    <form onSubmit={handleAuth} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="email">{t('fields.email')}</Label>
        <Input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="password">{t('fields.password')}</Label>
        <Input
          id="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        {!isSignUp && (
          <Button
            type="button"
            variant="link"
            className="text-sm p-0 h-auto font-normal"
            onClick={() => setForgotPassword(true)}
          >
            {t('buttons.forgotPassword')}
          </Button>
        )}
      </div>

      {isSignUp && (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="username">{t('fields.username')}</Label>
            <Input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              minLength={3}
              placeholder="Ex: johndoe123"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName">{t('fields.lastName')}</Label>
            <Input
              id="lastName"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              minLength={2}
              placeholder="Ex: Dupont"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="firstName">{t('fields.firstName')}</Label>
            <Input
              id="firstName"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              minLength={2}
              placeholder="Ex: Jean"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="residence-country">{t('fields.residenceCountry')}</Label>
            <Select value={residenceCountry} onValueChange={setResidenceCountry} required>
              <SelectTrigger id="residence-country">
                <SelectValue placeholder={t('fields.selectCountry')} />
              </SelectTrigger>
              <SelectContent>
                {COUNTRIES.map((country) => (
                  <SelectItem key={country.code} value={country.code}>
                    {country.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      <Button type="submit" className="w-full" disabled={loading}>
        {loading ? t('buttons.loading', { ns: 'common' }) : isSignUp ? t('buttons.signup') : t('buttons.login')}
      </Button>

      <Button
        type="button"
        variant="ghost"
        className="w-full"
        onClick={() => setIsSignUp(!isSignUp)}
      >
        {isSignUp
          ? t('buttons.alreadyHaveAccount')
          : t('buttons.noAccount')}
      </Button>
    </form>
  );
};
